import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FileText, CheckCircle, AlertTriangle, Clock, Send, Eye, Ban, CreditCard, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  sent: { label: 'Awaiting Payment', color: 'bg-blue-500', icon: Send },
  viewed: { label: 'Viewed', color: 'bg-purple-500', icon: Eye },
  paid: { label: 'Paid', color: 'bg-green-500', icon: CheckCircle },
  overdue: { label: 'Overdue', color: 'bg-red-500', icon: AlertTriangle },
  void: { label: 'Void', color: 'bg-gray-500', icon: Ban },
};

const paymentTermsLabels = {
  due_on_receipt: 'Due on Receipt',
  net_15: 'Net 15',
  net_30: 'Net 30'
};

// Format line item types to human-readable labels
const formatLineItemType = (type) => {
  const typeLabels = {
    'service': 'Process Service',
    'process_service': 'Process Service',
    'skip_trace': 'Skip Trace',
    'court_filing': 'Court Filing',
    'notary': 'Notary Service',
    'rush_fee': 'Rush Service Fee',
    'mileage': 'Mileage',
    'processing_fee': 'Processing Fee',
    'other': 'Other Service'
  };
  return typeLabels[type] || type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Service';
};

export default function PublicInvoicePage() {
  const { invoiceId } = useParams();
  const [searchParams] = useSearchParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  useEffect(() => {
    fetchInvoice();
  }, [invoiceId]);

  // Handle payment return
  useEffect(() => {
    const payment = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    
    if (payment === 'success' && sessionId) {
      // Poll for payment status
      pollPaymentStatus(sessionId);
    } else if (payment === 'cancelled') {
      setPaymentStatus({ type: 'cancelled', message: 'Payment was cancelled. You can try again when ready.' });
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [searchParams, invoiceId]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPaymentStatus({ type: 'timeout', message: 'Payment verification timed out. Please refresh the page to check your payment status.' });
      return;
    }

    try {
      setPaymentStatus({ type: 'checking', message: 'Verifying your payment...' });
      
      const response = await fetch(`${API_URL}/api/public/invoice/${invoiceId}/payment-status/${sessionId}`);
      const data = await response.json();

      if (data.payment_status === 'paid') {
        setPaymentStatus({ type: 'success', message: 'Payment successful! Thank you.' });
        // Refresh invoice to show updated status
        fetchInvoice();
        // Clean up URL
        window.history.replaceState({}, '', window.location.pathname);
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus({ type: 'error', message: 'Payment session expired. Please try again.' });
        window.history.replaceState({}, '', window.location.pathname);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (err) {
      console.error('Error checking payment status:', err);
      setPaymentStatus({ type: 'error', message: 'Error verifying payment. Please refresh the page.' });
    }
  };

  const handlePayNow = async () => {
    setPaymentLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/public/invoice/${invoiceId}/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ origin_url: window.location.origin, source: searchParams.get('src') || undefined })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to start payment');
      }

      const data = await response.json();
      // Redirect to Stripe Checkout
      window.location.href = data.checkout_url;
    } catch (err) {
      console.error('Payment error:', err);
      setPaymentStatus({ type: 'error', message: err.message || 'Failed to start payment. Please try again.' });
      setPaymentLoading(false);
    }
  };

  const fetchInvoice = async () => {
    try {
      const response = await fetch(`${API_URL}/api/public/invoice/${invoiceId}`);
      if (!response.ok) {
        throw new Error('Invoice not found');
      }
      const data = await response.json();
      // API returns { invoice, client, company, from, invoice_settings }
      setInvoice({
        ...data.invoice,
        client: data.client,
        company: data.company,  // Include company data
        server_profile: data.from,
        settings: data.invoice_settings
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-slate-300 border-t-slate-600 rounded-full"></div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <FileText className="h-16 w-16 text-slate-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Invoice Not Found</h1>
          <p className="text-slate-600 mb-6">This invoice may have been deleted or doesn't exist.</p>
          <Link to="/" className="text-teal-600 hover:text-teal-700 font-medium">
            Go to Servio Homepage
          </Link>
        </div>
      </div>
    );
  }

  const StatusIcon = statusConfig[invoice.status]?.icon || FileText;
  const settings = invoice.settings || {};
  const serverProfile = invoice.server_profile || {};
  
  const brandingName = settings.company_name || serverProfile.company || serverProfile.name || 'Process Server';
  const brandingLogo = settings.logo_url || null;
  const brandingTagline = settings.tagline || '';
  const defaultTerms = settings.default_terms || '';
  const footerPhone = settings.footer_phone || serverProfile.phone || '';
  const footerEmail = settings.footer_email || serverProfile.email || '';
  const footerWebsite = settings.footer_website || '';
  const footerText = settings.footer_text || '';

  // Theme settings
  const theme = settings.theme || {};
  const primaryColor = theme.primary_color || '#1e40af';
  const accentColor = theme.accent_color || '#3b82f6';
  const headerBgColor = theme.header_bg_color || '#f8fafc';
  const textColor = theme.text_color || '#1e293b';
  const fontFamily = theme.font_family || 'system';
  
  // Font family mapping
  const fontFamilyMap = {
    'system': '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    'inter': '"Inter", sans-serif',
    'roboto': '"Roboto", sans-serif',
    'open-sans': '"Open Sans", sans-serif',
    'lato': '"Lato", sans-serif',
    'poppins': '"Poppins", sans-serif',
    'playfair': '"Playfair Display", serif',
    'merriweather': '"Merriweather", serif',
    'georgia': 'Georgia, serif',
    'times': '"Times New Roman", Times, serif',
  };
  const fontFamilyStyle = fontFamilyMap[fontFamily] || fontFamilyMap['system'];

  const logoSizeClasses = {
    small: 'h-16 w-auto max-w-[120px]',
    medium: 'h-24 w-auto max-w-[200px]',
    large: 'h-32 w-auto max-w-[300px]',
    xlarge: 'h-48 w-auto max-w-[500px]'
  };
  const logoSize = settings.logo_size || 'medium';

  return (
    <>
      <Helmet>
        <title>{`Invoice ${invoice?.invoice_number || ''} | Servio`}</title>
      </Helmet>
      
      <div className="min-h-screen bg-slate-100 py-8 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Payment Status Banner */}
          {paymentStatus && (
            <div className={`mb-4 px-6 py-4 rounded-lg flex items-center gap-3 ${
              paymentStatus.type === 'success' ? 'bg-green-100 border border-green-300' :
              paymentStatus.type === 'checking' ? 'bg-blue-100 border border-blue-300' :
              paymentStatus.type === 'cancelled' ? 'bg-yellow-100 border border-yellow-300' :
              'bg-red-100 border border-red-300'
            }`}>
              {paymentStatus.type === 'checking' && <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />}
              {paymentStatus.type === 'success' && <CheckCircle className="h-5 w-5 text-green-600" />}
              {paymentStatus.type === 'cancelled' && <AlertTriangle className="h-5 w-5 text-yellow-600" />}
              {paymentStatus.type === 'error' && <AlertTriangle className="h-5 w-5 text-red-600" />}
              {paymentStatus.type === 'timeout' && <AlertTriangle className="h-5 w-5 text-orange-600" />}
              <span className={`font-medium ${
                paymentStatus.type === 'success' ? 'text-green-800' :
                paymentStatus.type === 'checking' ? 'text-blue-800' :
                paymentStatus.type === 'cancelled' ? 'text-yellow-800' :
                'text-red-800'
              }`}>{paymentStatus.message}</span>
            </div>
          )}

          {/* Status Banner */}
          <div className={`${statusConfig[invoice.status]?.color || 'bg-slate-500'} text-white px-6 py-3 rounded-t-lg flex items-center justify-between`}>
            <div className="flex items-center gap-2">
              <StatusIcon className="h-5 w-5" />
              <span className="font-medium">{statusConfig[invoice.status]?.label || invoice.status}</span>
            </div>
            <span className="text-sm opacity-90">Invoice {invoice.invoice_number}</span>
          </div>

          {/* Invoice Content */}
          <div 
            className="bg-white rounded-b-lg shadow-lg overflow-hidden"
            style={{ fontFamily: fontFamilyStyle, color: textColor }}
          >
            {/* Header - Logo/Company Name with matching width */}
            <div className="px-6 pt-4 pb-2">
              <div 
                className="rounded-lg py-5 text-center"
                style={{ backgroundColor: headerBgColor }}
              >
                {brandingLogo ? (
                  <div className="flex flex-col items-center">
                    <img 
                      src={brandingLogo} 
                      alt="Logo" 
                      className={`${logoSizeClasses[logoSize]} object-contain`}
                    />
                    {brandingTagline && <p className="text-sm mt-2" style={{ color: textColor, opacity: 0.6 }}>{brandingTagline}</p>}
                  </div>
                ) : (
                  <div>
                    <h1 className="text-2xl font-bold" style={{ color: primaryColor }}>{brandingName}</h1>
                    {brandingTagline && <p className="text-sm mt-1" style={{ color: textColor, opacity: 0.6 }}>{brandingTagline}</p>}
                  </div>
                )}
              </div>
            </div>

            {/* Invoice Meta Bar - Invoice #, Date, Due, Terms */}
            <div className="px-6 py-2">
              <div 
                className="rounded-lg border py-2.5"
                style={{ backgroundColor: headerBgColor, borderColor: accentColor + '40' }}
              >
                <div className="flex justify-between items-center text-center">
                  <div className="flex-1">
                    <p className="text-xs uppercase tracking-wider" style={{ color: textColor, opacity: 0.5 }}>Invoice #</p>
                    <p className="font-mono font-semibold text-sm">{invoice.invoice_number}</p>
                  </div>
                  <div className="flex-1 border-l" style={{ borderColor: accentColor + '40' }}>
                    <p className="text-xs uppercase tracking-wider" style={{ color: textColor, opacity: 0.5 }}>Issue Date</p>
                    <p className="font-semibold text-sm">{formatDate(invoice.invoice_date)}</p>
                  </div>
                  <div className="flex-1 border-l" style={{ borderColor: accentColor + '40' }}>
                    <p className="text-xs uppercase tracking-wider" style={{ color: textColor, opacity: 0.5 }}>Due Date</p>
                    <p className="font-semibold text-sm">{formatDate(invoice.due_date)}</p>
                  </div>
                  <div className="flex-1 border-l" style={{ borderColor: accentColor + '40' }}>
                    <p className="text-xs uppercase tracking-wider" style={{ color: textColor, opacity: 0.5 }}>Terms</p>
                    <p className="font-semibold text-sm">{paymentTermsLabels[invoice.payment_terms] || invoice.payment_terms}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="p-6">
              {/* From / Bill To - Side by Side */}
              <div className="grid grid-cols-2 gap-8 mb-4">
                <div>
                  <p className="text-xs uppercase tracking-wider font-medium mb-2" style={{ color: textColor, opacity: 0.5 }}>From</p>
                  <div>
                    <p className="font-semibold">{serverProfile.name || ''}</p>
                    {serverProfile.company && <p className="text-sm" style={{ opacity: 0.8 }}>{serverProfile.company}</p>}
                    {serverProfile.address && (
                      <>
                        {serverProfile.address.street && <p className="text-sm" style={{ opacity: 0.6 }}>{serverProfile.address.street}</p>}
                        <p className="text-sm" style={{ opacity: 0.6 }}>
                          {[serverProfile.address.city, serverProfile.address.state, serverProfile.address.zip_code].filter(Boolean).join(', ')}
                        </p>
                      </>
                    )}
                    {serverProfile.phone && <p className="text-sm" style={{ opacity: 0.6 }}>{serverProfile.phone}</p>}
                  </div>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider font-medium mb-2" style={{ color: textColor, opacity: 0.5 }}>Bill To</p>
                  <div>
                    {/* Show company name prominently if client is linked to a company */}
                    {invoice.company ? (
                      <>
                        <p className="font-semibold">{invoice.company.name}</p>
                        {invoice.client?.name && <p className="text-sm" style={{ opacity: 0.8 }}>{invoice.client.name}{invoice.client?.title ? ` (${invoice.client.title})` : ''}</p>}
                        {(invoice.company.address_street || invoice.client?.address_street) && (
                          <p className="text-sm" style={{ opacity: 0.6 }}>
                            {invoice.client?.address_street || invoice.company.address_street}
                          </p>
                        )}
                        <p className="text-sm" style={{ opacity: 0.6 }}>
                          {[
                            invoice.client?.address_city || invoice.company.address_city, 
                            invoice.client?.address_state || invoice.company.address_state, 
                            invoice.client?.address_zip || invoice.company.address_zip
                          ].filter(Boolean).join(', ')}
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="font-semibold">{invoice.client?.name}</p>
                        {invoice.client?.company_name && <p className="text-sm" style={{ opacity: 0.8 }}>{invoice.client.company_name}</p>}
                        {invoice.client?.address_street && <p className="text-sm" style={{ opacity: 0.6 }}>{invoice.client.address_street}</p>}
                        <p className="text-sm" style={{ opacity: 0.6 }}>
                          {[invoice.client?.address_city, invoice.client?.address_state, invoice.client?.address_zip].filter(Boolean).join(', ')}
                        </p>
                      </>
                    )}
                    {invoice.client?.email && <p className="text-sm" style={{ color: accentColor }}>{invoice.client.email}</p>}
                  </div>
                </div>
              </div>

              {/* Optional Fields - Entity Served, Case #, Ref #, Service Date - BELOW From/Bill To */}
              {(invoice.entity_served || invoice.case_number || invoice.reference_number || invoice.service_date) && (
                <div 
                  className="rounded-lg border py-2.5 px-4 mb-6"
                  style={{ backgroundColor: headerBgColor, borderColor: accentColor + '40' }}
                >
                  <div className="flex justify-between items-center text-center">
                    {invoice.entity_served && (
                      <div className="flex-1">
                        <p className="text-xs uppercase tracking-wider" style={{ color: textColor, opacity: 0.5 }}>Entity Served</p>
                        <p className="font-semibold text-sm">{invoice.entity_served}</p>
                      </div>
                    )}
                    {invoice.case_number && (
                      <div className={`flex-1 ${invoice.entity_served ? 'border-l' : ''}`} style={{ borderColor: accentColor + '30' }}>
                        <p className="text-xs uppercase tracking-wider" style={{ color: textColor, opacity: 0.5 }}>Case #</p>
                        <p className="font-semibold text-sm">{invoice.case_number}</p>
                      </div>
                    )}
                    {invoice.reference_number && (
                      <div className={`flex-1 ${(invoice.entity_served || invoice.case_number) ? 'border-l' : ''}`} style={{ borderColor: accentColor + '30' }}>
                        <p className="text-xs uppercase tracking-wider" style={{ color: textColor, opacity: 0.5 }}>Ref #</p>
                        <p className="font-semibold text-sm">{invoice.reference_number}</p>
                      </div>
                    )}
                    {invoice.service_date && (
                      <div className={`flex-1 ${(invoice.entity_served || invoice.case_number || invoice.reference_number) ? 'border-l' : ''}`} style={{ borderColor: accentColor + '30' }}>
                        <p className="text-xs uppercase tracking-wider" style={{ color: textColor, opacity: 0.5 }}>Service Date</p>
                        <p className="font-semibold text-sm">{formatDate(invoice.service_date)}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Line Items */}
              <div className="border-b-2 pb-2 mb-3" style={{ borderColor: primaryColor }}>
                <p className="text-sm font-semibold uppercase tracking-wider" style={{ color: primaryColor }}>Services & Charges</p>
              </div>

              <table className="w-full mb-4">
                <thead>
                  <tr className="text-xs uppercase" style={{ color: textColor, opacity: 0.5 }}>
                    <th className="text-left py-2 font-medium">Description</th>
                    <th className="text-right py-2 font-medium w-16">Qty</th>
                    <th className="text-right py-2 font-medium w-24">Rate</th>
                    <th className="text-right py-2 font-medium w-24">Amount</th>
                  </tr>
                </thead>
                <tbody style={{ borderColor: accentColor + '20' }}>
                  {invoice.line_items?.map((item, idx) => {
                    const isProcessingFee = item.item_type === 'processing_fee';
                    return (
                      <tr key={idx} className="border-b" style={{ borderColor: accentColor + '20' }}>
                        <td className="py-3">
                          <p className="font-medium">
                            {item.description || (isProcessingFee ? `Processing Fee (${item.unit_price}%)` : formatLineItemType(item.item_type))}
                          </p>
                        </td>
                        <td className="py-3 text-right" style={{ opacity: 0.7 }}>
                          {isProcessingFee ? '-' : item.quantity}
                        </td>
                        <td className="py-3 text-right" style={{ opacity: 0.7 }}>
                          {isProcessingFee ? `${item.unit_price}%` : formatCurrency(item.unit_price)}
                        </td>
                        <td className="py-3 text-right font-semibold">{formatCurrency(item.total)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Totals Section - Two column layout when payments exist */}
              <div className="border-t pt-3" style={{ borderColor: accentColor + '40' }}>
                <div className={`flex ${invoice.payments && invoice.payments.length > 0 ? 'justify-between gap-6' : 'justify-end'}`}>
                  {/* Payments Received - Left side */}
                  {invoice.payments && invoice.payments.length > 0 && (
                    <div className="flex-1 max-w-[55%]">
                      <p className="text-xs uppercase tracking-wider font-medium mb-2" style={{ color: textColor, opacity: 0.5 }}>Payments Received</p>
                      {invoice.payments.map((payment, idx) => {
                        let dateStr = 'Date N/A';
                        const paymentDate = payment.payment_date || payment.date;
                        if (paymentDate) {
                          const d = new Date(paymentDate);
                          if (!isNaN(d.getTime())) {
                            dateStr = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
                          }
                        }
                        const method = payment.payment_method || payment.method;
                        return (
                          <div key={idx} className="flex justify-between items-center text-sm py-1">
                            <span style={{ opacity: 0.7 }}>
                              {dateStr}
                              {method && <span style={{ opacity: 0.7 }}> ({method})</span>}
                            </span>
                            <span className="text-green-600 font-medium">-{formatCurrency(payment.amount)}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Totals - Right side */}
                  <div className={invoice.payments && invoice.payments.length > 0 ? 'w-48' : 'w-56'}>
                    {invoice.tax_rate > 0 && (
                      <>
                        <div className="flex justify-between py-1.5 text-sm">
                          <span style={{ opacity: 0.6 }}>Subtotal</span>
                          <span>{formatCurrency(invoice.subtotal)}</span>
                        </div>
                        <div className="flex justify-between py-1.5 text-sm">
                          <span style={{ opacity: 0.6 }}>Tax ({invoice.tax_rate}%)</span>
                          <span>{formatCurrency(invoice.tax_amount)}</span>
                        </div>
                      </>
                    )}
                    <div className={`flex justify-between py-2 ${invoice.tax_rate > 0 ? 'mt-1 border-t-2' : ''}`} style={{ borderColor: primaryColor }}>
                      <span className="font-bold">Total</span>
                      <span className="font-bold text-lg" style={{ color: primaryColor }}>{formatCurrency(invoice.total)}</span>
                    </div>
                    {/* Balance Due - show when there are payments */}
                    {invoice.payments && invoice.payments.length > 0 && (
                      <div className="flex justify-between py-2 mt-1 border-t-2" style={{ borderColor: primaryColor }}>
                        <span className="font-bold">Balance Due</span>
                        <span className={`font-bold text-lg ${(invoice.total - (invoice.total_paid || 0)) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {formatCurrency(invoice.total - (invoice.total_paid || 0))}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Notes */}
              {invoice.notes && (
                <div className="mt-6 pt-4 border-t" style={{ borderColor: accentColor + '40' }}>
                  <p className="text-xs uppercase tracking-wider font-medium mb-1" style={{ color: textColor, opacity: 0.5 }}>Notes</p>
                  <p className="whitespace-pre-wrap text-sm" style={{ opacity: 0.7 }}>{invoice.notes}</p>
                </div>
              )}

              {/* Terms */}
              {defaultTerms && (
                <div className="mt-4 pt-4 border-t" style={{ borderColor: accentColor + '40' }}>
                  <p className="text-xs uppercase tracking-wider font-medium mb-1" style={{ color: textColor, opacity: 0.5 }}>Terms & Conditions</p>
                  <p className="whitespace-pre-wrap text-sm" style={{ opacity: 0.7 }}>{defaultTerms}</p>
                </div>
              )}

              {/* Void Info */}
              {invoice.status === 'void' && invoice.void_info && (
                <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-700 font-medium">Invoice Voided</p>
                  <p className="text-red-600 text-sm mt-1">Reason: {invoice.void_info}</p>
                  {invoice.voided_at && (
                    <p className="text-red-500 text-xs mt-1">Voided: {formatDate(invoice.voided_at)}</p>
                  )}
                </div>
              )}

              {/* Pay Now Button - Show for unpaid invoices with online payments enabled */}
              {settings?.stripe_connect_enabled && 
               invoice.status !== 'paid' && 
               invoice.status !== 'void' &&
               (invoice.total - (invoice.total_paid || 0)) > 0 && (
                <div className="mt-6 pt-4 border-t" style={{ borderColor: accentColor + '40' }}>
                  {(invoice.total - (invoice.total_paid || 0)) >= 0.50 ? (
                    <>
                      <button
                        onClick={handlePayNow}
                        disabled={paymentLoading}
                        className="w-full py-4 px-6 rounded-lg font-semibold text-white text-lg flex items-center justify-center gap-3 transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ backgroundColor: primaryColor }}
                        data-testid="pay-now-button"
                      >
                        {paymentLoading ? (
                          <>
                            <Loader2 className="h-5 w-5 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <CreditCard className="h-5 w-5" />
                            Pay Now - {formatCurrency(invoice.total - (invoice.total_paid || 0))}
                          </>
                        )}
                      </button>
                      <p className="text-center text-xs mt-2" style={{ color: textColor, opacity: 0.5 }}>
                        Secure payment powered by Stripe
                      </p>
                    </>
                  ) : (
                    <p className="text-center text-sm py-3 px-4 bg-slate-100 rounded-lg" style={{ color: textColor, opacity: 0.7 }}>
                      Online payment unavailable for amounts under $0.50. Please use an alternative payment method.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Footer - matching header width */}
            {(footerPhone || footerEmail || footerWebsite || footerText) && (
              <div className="px-6 pb-4 pt-2">
                <div 
                  className="rounded-lg py-4"
                  style={{ backgroundColor: headerBgColor }}
                >
                  {(footerPhone || footerEmail || footerWebsite) && (
                    <div className="flex flex-wrap justify-center gap-x-6 gap-y-1 text-sm mb-2" style={{ color: textColor, opacity: 0.6 }}>
                      {footerPhone && <span>{footerPhone}</span>}
                      {footerEmail && <span>{footerEmail}</span>}
                      {footerWebsite && <span>{footerWebsite}</span>}
                    </div>
                  )}
                  <p className="text-sm text-center" style={{ color: textColor, opacity: 0.5 }}>
                    {footerText || 'Thank you for your business'}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Powered by */}
          <div className="mt-6 text-center">
            <Link to="/" className="text-sm text-slate-400 hover:text-slate-600">
              Powered by <span className="font-semibold">Servio</span>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
